import { ProductResult } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { cn } from '@shared/lib/utils';
import ProductMetric from './product-metric';

const CAR_KG_CO2E_PER_MILE = 0.33994;
interface ProductDetailTotalEmissionCardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  productResult: ProductResult;
  pdf?: boolean;
}
export function ProductDetailTotalEmissionCard({
  productResult,
  className,
  pdf = false,
}: ProductDetailTotalEmissionCardProps) {
  const exactMiles = productResult.totalEmissions! / CAR_KG_CO2E_PER_MILE;
  const roundedMiles = Math.round(exactMiles);
  const isSubOne = roundedMiles === 0 && exactMiles > 0;
  const milesLabel = isSubOne
    ? '<1 mile'
    : `~${roundedMiles} ${roundedMiles === 1 ? 'mile' : 'miles'}`;
  return (
    <div
      className={cn(
        'flex w-full flex-col space-y-4 rounded-md px-3 py-3 bg-gray-00',
        { 'p-0 space-y-2': pdf },
        className
      )}
    >
      <div className="flex w-full items-center justify-between">
        <Text
          variant="cardTitle"
          className={cn('text-primary-900', {
            'font-heading text-md font-bold text-primary-700': pdf,
          })}
        >
          Total emissions
        </Text>
      </div>
      <Text variant="subtle" className={cn({ 'text-xs': pdf })}>
        The overall emissions per unit produced, rather than per kilogram.
      </Text>

      <div className="flex">
        <ProductMetric
          className="flex-1 space-y-0"
          title="Total emissions"
          value={productResult.totalEmissions!}
          unit="kgCO2e"
        />
        <ProductMetric
          className="flex-1 space-y-0"
          title="Emissions per serving"
          value={productResult.totalEmissionsPerServing!}
          unit="kgCO2e"
        />
      </div>
      <SimpleTooltip
        trigger={
          <div className="flex items-center gap-2 rounded-md bg-teal-50 p-2 py-1">
            <Icons.car className="text-teal-600" size={16} />
            <Text variant="subtle" className="text-teal-600">
              Equivalent to driving {milesLabel} in a car
            </Text>
          </div>
        }
        content={
          <div className="space-y-1">
            <p>Equivalent distance: {exactMiles.toFixed(1)} miles</p>
            <p>
              Calculated using the Defra 2025 emission factor for an average car
            </p>
          </div>
        }
        side="bottom"
      />
    </div>
  );
}
